// Голосовой ввод в поле: запись → расшифровка (Gemini, api /api/threads/transcribe) → текст в поле. Отправляет
// владелец сам, поправив, если нужно. Один и тот же ввод — в поле чата и в ответе агенту из карточки «Решений».
import { toast } from '@autoaff/ui';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { micError, type Recording, startRecording, VOICE_MAX_SECONDS } from './voice';

export interface VoiceInput {
  /** idle — не пишет; rec — пишет; busy — расшифровывает. */
  state: 'idle' | 'rec' | 'busy';
  /** Сколько секунд идёт запись. */
  secs: number;
  /** Начать запись или закончить и расшифровать. */
  toggle: () => void;
  /** Бросить запись без расшифровки. */
  cancel: () => void;
}

export function useVoiceInput(onText: (heard: string) => void): VoiceInput {
  const rec = useRef<{ r: Recording; since: number } | null>(null);
  const [state, setState] = useState<VoiceInput['state']>('idle');
  const [secs, setSecs] = useState(0);
  const cb = useRef(onText);
  cb.current = onText;
  const finish = async () => {
    const cur = rec.current;
    if (!cur) return;
    rec.current = null;
    setState('busy');
    try {
      const { text } = await api<{ text: string }>('/api/threads/transcribe', cur.r.stop());
      cb.current(text);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setState('idle');
    }
  };
  const cancel = () => {
    rec.current?.r.cancel();
    rec.current = null;
    setState('idle');
  };
  const toggle = async () => {
    if (state === 'busy') return;
    if (rec.current) return void finish();
    try {
      rec.current = { r: await startRecording(), since: Date.now() };
      setSecs(0);
      setState('rec');
    } catch (e) {
      toast(micError(e));
    }
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: таймер живёт, пока идёт запись
  useEffect(() => {
    if (state !== 'rec') return;
    const t = setInterval(() => {
      const s = rec.current ? Math.floor((Date.now() - rec.current.since) / 1000) : 0;
      setSecs(s);
      if (s >= VOICE_MAX_SECONDS) void finish();
    }, 250);
    return () => clearInterval(t);
  }, [state]);
  useEffect(() => () => rec.current?.r.cancel(), []);
  return { state, secs, toggle: () => void toggle(), cancel };
}
